import { useCallback, useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import type { AiSettings, CheckAiKeyResult } from '@mlc/shared';
import { checkAiKey, readInstanceSettings, updateInstanceSettings } from './api';
import { TextSizeControl } from './CookMode';
import { useCookTextSize } from './cookTextSize';
import { useUnitSystem } from './unitSystem';
import { DataBackup } from './DataBackup';
import { GestionMembres, MonCompte } from './MemberSettings';
import { BienDemarrerCartes } from './BienDemarrer';
import { DeviceMode } from './DeviceMode';
import { ShortcutSetup } from './ShortcutSetup';
import { useSession } from './session';
import { useLectureVocale } from './speech';
import { TagManager } from './TagManager';
import { Banner, Button, FOCUS_RING, PageHeader, SegmentedControl, TextInput } from './ui';
import { ThemeToggle } from './ThemeToggle';
import { formaterDateConstruction, useVersion } from './version';

export interface SettingsProps {
  onImported: () => void;
}

/**
 * Réglage de la lecture à voix haute (US-60). Deux boutons construits par le
 * même mécanisme que les unités : un réglage à deux états se lit mieux en deux
 * boutons qu'en une case dont on doit deviner si elle allume ou éteint.
 *
 * Absent quand l'appareil ne sait pas parler : proposer un réglage sans effet
 * est pire que de ne pas le proposer.
 */
function LectureVocaleReglage(): React.JSX.Element | null {
  const { actif, setActif, possible } = useLectureVocale();
  if (!possible) return null;

  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-heading-3">Lecture à voix haute</h3>
      <p className="text-sm text-foreground-secondary">
        En mode cuisine, chaque étape est lue à voix haute à son arrivée. Sans ce réglage, vous pouvez toujours dire
        « répète » pour vous faire relire l’étape en cours.
      </p>
      {/* Même bascule que les unités et le thème, donc même contrôle. */}
      <SegmentedControl
        label="Lecture à voix haute"
        value={actif ? 'oui' : 'non'}
        onChange={(valeur) => { setActif(valeur === 'oui'); }}
        options={[
          { value: 'non', label: 'Silencieuse' },
          { value: 'oui', label: 'Lire les étapes' },
        ]}
        className="self-start"
      />
    </div>
  );
}

/**
 * Ce que le bandeau de vérification dit, selon l'issue (US-69). Trois issues
 * et trois tons : se tromper de clé et n'avoir pas de réseau appellent deux
 * gestes différents, et le bandeau ne doit pas les confondre.
 */
const TON_VERIFICATION: Record<CheckAiKeyResult, 'success' | 'danger' | 'warning'> = {
  valid: 'success',
  refused: 'danger',
  unreachable: 'warning',
};

/**
 * Assistance IA (US-69).
 *
 * `CLAUDE.md` §1 promettait depuis le premier jour une clé « fournie par
 * l'utilisateur dans les réglages ». Faute de cet écran — et de la moindre
 * mention dans `.env.example` — la clé n'a jamais été posée nulle part, et
 * trois fonctions écrites et testées n'ont jamais été exécutées une seule fois.
 *
 * ### Trois choix d'affichage, et leurs raisons
 *
 * **La clé ne se relit pas.** Le champ est toujours vide à l'ouverture : ce
 * n'est pas un formulaire d'édition, c'est un endroit où l'on colle. L'état
 * courant se lit à côté — configurée ou non, d'où elle vient, ses quatre
 * derniers caractères — et cela suffit à reconnaître la clé qu'on a collée.
 *
 * **L'écran dit que l'administrateur peut la lire** (§1 « Honnêteté »). Aucun
 * cadenas, aucun « stockée de façon sécurisée » : c'est une promesse que le
 * système ne peut pas tenir, et la suggérer serait pire que se taire.
 *
 * **L'environnement gagne, et l'écran le dit.** Sans cette phrase, une clé
 * saisie ici pendant qu'une variable est posée sur le serveur resterait sans
 * effet, et l'écran passerait pour cassé.
 */
function AssistanceIa(): React.JSX.Element {
  const [etat, setEtat] = useState<AiSettings | null>(null);
  const [cle, setCle] = useState('');
  const [modele, setModele] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [verification, setVerification] = useState<{ ton: 'success' | 'danger' | 'warning'; message: string } | null>(null);
  const [occupe, setOccupe] = useState(false);

  const echec = useCallback((e: unknown) => {
    setErreur(e instanceof Error ? e.message : String(e));
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    readInstanceSettings(controller.signal).then(({ ai }) => { setEtat(ai); }).catch(() => {
      // Rien à afficher : l'écran entier est déjà réservé au propriétaire, et
      // un échec de lecture ne doit pas transformer les réglages en page
      // d'erreur.
    });
    return () => { controller.abort(); };
  }, []);

  function enregistrer(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setErreur(null);
    setNotice(null);
    setVerification(null);
    setOccupe(true);
    updateInstanceSettings({ anthropic_api_key: cle })
      .then(({ ai }) => {
        setEtat(ai);
        setCle('');
        setNotice('Clé enregistrée. L’import photo et le repli IA de l’import web sont disponibles tout de suite.');
      })
      .catch(echec)
      .finally(() => { setOccupe(false); });
  }

  function retirer(): void {
    setErreur(null);
    setVerification(null);
    setOccupe(true);
    updateInstanceSettings({ anthropic_api_key: null })
      .then(({ ai }) => {
        setEtat(ai);
        setNotice('Clé retirée. Les fonctions qui en dépendent disparaissent de l’application.');
      })
      .catch(echec)
      .finally(() => { setOccupe(false); });
  }

  function enregistrerModele(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setErreur(null);
    setNotice(null);
    setOccupe(true);
    updateInstanceSettings({ anthropic_model: modele })
      .then(({ ai }) => {
        setEtat(ai);
        setModele('');
        setNotice(`Modèle enregistré : ${ai.model}.`);
      })
      .catch(echec)
      .finally(() => { setOccupe(false); });
  }

  function verifier(): void {
    setErreur(null);
    setNotice(null);
    setVerification(null);
    setOccupe(true);
    checkAiKey()
      .then((resultat) => { setVerification({ ton: TON_VERIFICATION[resultat.result], message: resultat.message }); })
      .catch(echec)
      .finally(() => { setOccupe(false); });
  }

  if (etat === null) {
    return <p className="text-sm text-foreground-secondary">Réglages de l’assistance IA en cours de lecture…</p>;
  }

  const parEnvironnement = etat.key_source === 'environment';

  return (
    <div className="flex flex-col gap-3">
      {erreur !== null && <Banner tone="danger">{erreur}</Banner>}
      {notice !== null && <Banner tone="success">{notice}</Banner>}
      {verification !== null && <Banner tone={verification.ton}>{verification.message}</Banner>}

      <p className="text-sm text-foreground-secondary">
        Sans clé, l’application fonctionne entièrement — l’import depuis une photo et le repli automatique de
        l’import web n’existent simplement pas, et leurs entrées ne s’affichent pas.
      </p>

      <p className="text-sm text-foreground-secondary">
        {etat.key_configured ? (
          <>
            Clé <strong>configurée</strong>, se terminant par <span className="text-data">{etat.key_hint}</span>
            {parEnvironnement
              ? ' — posée dans l’environnement du serveur.'
              : ' — saisie ici.'}
          </>
        ) : (
          <>Aucune clé <strong>configurée</strong> : les fonctions assistées par l’IA sont absentes.</>
        )}
        {' '}Modèle utilisé : <span className="text-data">{etat.model}</span>
        {etat.model_source === 'default' && ' (valeur par défaut)'}.
      </p>

      {parEnvironnement && (
        <p className="text-sm text-foreground-secondary">
          La variable d’environnement l’emporte sur ce qui est saisi ici : c’est ce qui permet de retirer la clé
          depuis le serveur sans ouvrir l’application. Une clé enregistrée ici reprendra effet le jour où la
          variable sera retirée.
        </p>
      )}

      {/* §1 « Honnêteté » : la seule promesse que le système ne peut pas tenir
          est celle qu'on ne fait pas. */}
      <p className="text-sm text-foreground-secondary">
        Cette clé est stockée en clair sur le serveur : la personne qui administre l’instance peut la lire, comme
        elle peut lire les recettes et les photos. Utilisez une clé dédiée à cette application, révocable seule.
      </p>

      <form onSubmit={enregistrer} className="flex flex-wrap items-end gap-2">
        <label className="flex flex-1 flex-col gap-1 text-sm">
          Clé d’API Anthropic
          <TextInput
            type="password"
            autoComplete="off"
            placeholder={etat.key_configured ? 'Coller une nouvelle clé' : 'sk-ant-…'}
            value={cle}
            onChange={(e) => { setCle(e.target.value); }}
          />
        </label>
        <Button type="submit" variant="secondary" disabled={occupe || cle.trim() === ''} aria-busy={occupe}>
          Enregistrer la clé
        </Button>
      </form>

      <div className="flex flex-wrap gap-2">
        {/* « Vérifier » : une clé fausse se découvre ici, pas au milieu d'un import. */}
        <Button variant="secondary" onClick={verifier} disabled={occupe || !etat.key_configured} aria-busy={occupe}>
          Vérifier
        </Button>
        {etat.key_source === 'settings' && (
          <Button variant="ghost" onClick={retirer} disabled={occupe} aria-busy={occupe}>
            Retirer la clé
          </Button>
        )}
      </div>

      <form onSubmit={enregistrerModele} className="flex flex-wrap items-end gap-2">
        <label className="flex flex-1 flex-col gap-1 text-sm">
          Modèle
          <TextInput
            type="text"
            autoComplete="off"
            placeholder={etat.model}
            value={modele}
            onChange={(e) => { setModele(e.target.value); }}
          />
        </label>
        <Button type="submit" variant="secondary" disabled={occupe || modele.trim() === ''} aria-busy={occupe}>
          Enregistrer le modèle
        </Button>
      </form>
    </div>
  );
}

/**
 * Réglages (US-24, redécoupés en US-57).
 *
 * ### Ce que l'écran était
 *
 * Sept sections en colonne unique, **trente-quatre contrôles**, qui mêlaient
 * quatre natures sans le dire : deux sections pour un seul sujet (« Apparence »
 * et « Unités » sont toutes deux des préférences d'affichage), une section de
 * gestion de contenu au milieu (« Étiquettes », qui n'est pas un réglage), et
 * surtout **le compte personnel et l'administration de l'instance dans la même
 * section**, séparés d'un simple `<h3>`.
 *
 * ### Quatre groupes, et la question qu'ils répondent
 *
 * - **Mon compte** — qui suis-je ici, et comment je m'en vais.
 * - **Mes préférences** — comment l'application se présente à moi. Le thème et
 *   les unités sont le même sujet ; ils avaient deux sections.
 * - **La maison** — ce que j'administre pour les autres. Propriétaire
 *   uniquement, et c'est la seule frontière qui change selon qui regarde.
 * - **Données et à propos** — la sauvegarde, la version.
 *
 * ### Le lien « ← Bibliothèque » a disparu
 *
 * La navigation est présente sur tous les écrans depuis US-56 : un lien de
 * retour en tête d'écran n'est plus qu'un doublon, et il occupait la place où
 * l'on cherche le titre.
 */
export function Settings({ onImported }: SettingsProps): React.JSX.Element {
  // Un appareil en mode partagé (US-50) n'a ni compte, ni membres, ni
  // sauvegarde : les lui montrer en erreur serait pire que ne pas les montrer.
  // Ces sections **disparaissent** plutôt que de s'afficher désactivées — un
  // écran de cuisine n'a pas à porter des commandes que personne n'y utilisera.
  const session = useSession();
  const partage = session?.device != null;
  const proprietaire = session?.member?.role === 'proprietaire';
  const cookTextSize = useCookTextSize();
  const { system, setSystem } = useUnitSystem();
  const { sante } = useVersion();
  const dateConstruction = sante?.built_at === undefined ? null : formaterDateConstruction(sante.built_at);

  /*
    **Sommaire latéral** (refonte éditoriale).

    L'écran restait, malgré le redécoupage d'US-57, **un seul défilement de
    quatre sections et d'une trentaine de contrôles** : sur un compte
    propriétaire, 2 400 px de haut, sans aucun repère de position ni moyen
    d'atteindre « Données et à propos » autrement qu'en faisant défiler devant
    la gestion des membres et l'éditeur de règles d'étiquettes. C'est ce qui
    lui donnait, plus que sa densité, son allure d'interface d'administration :
    non pas trop de réglages, mais **aucune carte pour s'y retrouver**.

    Ce sommaire n'est pas un jeu d'onglets : **toutes les sections restent
    montées et lisibles**, ce sont des ancres. Un lecteur d'écran continue de
    parcourir l'écran d'un bout à l'autre, la recherche du navigateur trouve
    tout, et rien ne se cache derrière un clic. La seule chose qui change est
    qu'on peut sauter.

    Les entrées sont dérivées des mêmes conditions que les sections — un
    sommaire qui pointerait vers « La maison » chez un membre non
    propriétaire mènerait à du vide.
  */
  const sommaire = [
    !partage ? { href: '#titre-compte', label: 'Mon compte' } : null,
    // US-72. Personnel, comme le sas qu'il remplit — chacun installe le sien —
    // et donc hors de « La maison », où vit le mode cuisine partagée.
    !partage ? { href: '#titre-telephone', label: 'Depuis mon téléphone' } : null,
    { href: '#titre-preferences', label: 'Mes préférences' },
    proprietaire && !partage ? { href: '#titre-maison', label: 'La maison' } : null,
    !partage ? { href: '#titre-donnees', label: 'Données et à propos' } : null,
    partage ? { href: '#titre-appareil-partage', label: 'Cet appareil' } : null,
  ].filter((entree): entree is { href: string; label: string } => entree !== null);

  return (
    <div className="flex flex-col gap-8">
      <PageHeader title="Réglages" lede="Votre compte, vos préférences d’affichage, et ce que vous administrez pour la famille." />

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[13rem_minmax(0,1fr)] lg:gap-12">
        {/* Collant sous l'en-tête sur grand écran ; sur téléphone, une rangée
            défilante posée en haut du contenu — la même liste, jamais montée
            deux fois. */}
        <nav
          aria-label="Sections des réglages"
          className="-mx-6 overflow-x-auto px-6 lg:mx-0 lg:overflow-visible lg:px-0 lg:sticky lg:top-8 lg:self-start"
        >
          <ul className="flex gap-1 lg:flex-col">
            {sommaire.map((entree) => (
              <li key={entree.href}>
                <a
                  href={entree.href}
                  className={`flex min-h-touch-min items-center whitespace-nowrap rounded-control px-3 text-sm font-medium text-foreground-secondary no-underline transition duration-(--duration-quick) ease-entrance hover:bg-surface-sunken hover:text-foreground ${FOCUS_RING}`}
                >
                  {entree.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        {/* `max-w-reading` sur la colonne de contenu : sans plafond, une
            adresse e-mail se saisissait dans un champ de 1050 px de large.
            §18 de la mission — « évite les formulaires étirés sur toute la
            largeur » — vaut ici, alors que la largeur *de l'écran*, elle,
            était nécessaire au sommaire. Ce sont deux questions distinctes, et
            les traiter comme une seule est ce qui avait fait ranger les
            réglages parmi les écrans étroits. */}
        <div className="flex min-w-0 max-w-reading flex-col gap-10">
      {!partage && (
        <section aria-labelledby="titre-compte" className="flex flex-col gap-3">
          <h2 id="titre-compte" className="scroll-mt-8 text-heading-2">Mon compte</h2>
          <MonCompte />
        </section>
      )}

      {/* Le raccourci « Ajouter à Mijoté » (US-72). Sa place est ici et non
          dans « La maison » : le jeton nomme une personne, la capture atterrit
          dans son sas, et chacun installe le sien. Le mode cuisine partagée,
          lui, ouvre un appareil au livre commun — c'est un réglage de la
          maison, et il y reste. */}
      {!partage && (
        <section aria-labelledby="titre-telephone" className="flex flex-col gap-3">
          <h2 id="titre-telephone" className="scroll-mt-8 text-heading-2">Depuis mon téléphone</h2>
          <ShortcutSetup />
        </section>
      )}

      {/* Le thème et les unités sont le même sujet — comment l'application se
          présente à moi — et occupaient deux sections. */}
      <section aria-labelledby="titre-preferences" className="flex flex-col gap-6">
        <h2 id="titre-preferences" className="scroll-mt-8 text-heading-2">Mes préférences</h2>

        <div className="flex flex-col gap-3">
          <h3 className="text-heading-3">Thème</h3>
          <p className="text-sm text-foreground-secondary">
            « Système » suit le réglage de votre téléphone ou de votre ordinateur.
          </p>
          <ThemeToggle />
        </div>

        {/* Taille du texte du mode cuisine (US-25). Elle ne se réglait que
            depuis le mode cuisine lui-même : il fallait ouvrir une recette et
            lancer une cuisson pour atteindre le seul endroit où la changer. */}
        <div className="flex flex-col gap-3">
          <h3 className="text-heading-3">Texte du mode cuisine</h3>
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-sm text-foreground-secondary">
              Taille : <span className="text-data">{cookTextSize.size} px</span>
            </p>
            <TextSizeControl
              size={cookTextSize.size}
              increase={cookTextSize.increase}
              decrease={cookTextSize.decrease}
            />
            <p aria-hidden="true" style={{ fontSize: cookTextSize.size }} className="text-foreground">
              Couper les légumes en dés.
            </p>
          </div>
        </div>

        {/* Lecture à voix haute (US-60). **Éteinte par défaut** : une
            application qui se met à parler sans qu'on l'ait demandé est une
            mauvaise surprise, et dans une cuisine où quelqu'un dort à l'étage,
            une faute. La commande vocale « répète » relit l'étape à la
            demande sans ce réglage — c'est l'usage courant ; celui-ci est pour
            qui veut être accompagné du début à la fin. */}
        <LectureVocaleReglage />

        {/* Unités (US-34) : un affichage, jamais un enregistrement — les
            recettes gardent les unités dans lesquelles elles ont été écrites. */}
        <div className="flex flex-col gap-3">
          <h3 className="text-heading-3">Unités</h3>
          <p className="text-sm text-foreground-secondary">
            Change seulement l’affichage. Vos recettes gardent les unités dans lesquelles elles ont été écrites, et
            ce qui ne se convertit pas — une gousse, un pot de yaourt — n’est jamais converti.
          </p>
          {/* Deux boutons construits par le même mécanisme (US-57) : « Métrique »
              était un bouton et « Impérial » un lien souligné — deux boutons
              radio dont un n'en était pas un.
              Depuis la refonte éditoriale, c'est un **sélecteur segmenté** :
              l'option active se signalait par un aplat orange plein, la
              variante réservée à l'action que la page existe pour faire. Trois
              choix exclusifs traités ainsi sur le même écran — thème, voix,
              unités — y posaient trois aplats de la couleur d'accent. */}
          <SegmentedControl
            label="Système d’unités"
            value={system}
            onChange={setSystem}
            options={[
              { value: 'metrique', label: 'Métrique (g, ml)' },
              { value: 'imperial', label: 'Impérial (oz, cups)' },
            ]}
            className="self-start"
          />
        </div>
      </section>

      {/* La maison : ce qu'on administre pour les autres. Propriétaire
          uniquement — c'est la seule frontière de cet écran qui dépend de qui
          regarde, et elle mérite d'être visible plutôt que dispersée. */}
      {!partage && proprietaire && (
        <section aria-labelledby="titre-maison" className="flex flex-col gap-6">
          <h2 id="titre-maison" className="scroll-mt-8 text-heading-2">La maison</h2>

          <div className="flex flex-col gap-3">
            <h3 className="text-heading-3">Membres</h3>
            <GestionMembres />
          </div>

          {/* Les étiquettes ne sont pas un réglage d'affichage : c'est du
              rangement de contenu, et il n'était pas à sa place entre le thème
              et le compte. `TagManager` était de surcroît monté hors du bloc
              réservé aux comptes, si bien qu'une tablette en mode cuisine
              partagée affichait « Renommer » et « Supprimer » — dont l'usage
              retournait 404. */}
          <div className="flex flex-col gap-3">
            <h3 className="text-heading-3">Étiquettes</h3>
            <TagManager />
          </div>

          <div className="flex flex-col gap-3">
            <h3 className="text-heading-3">Mode cuisine partagée</h3>
            <DeviceMode />
          </div>

          {/* Assistance IA (US-69). Dans « La maison » et nulle part ailleurs :
              c'est un réglage de l'instance, pas une préférence d'affichage, et
              ses routes répondent 404 à qui n'est pas propriétaire. */}
          <div className="flex flex-col gap-3">
            <h3 className="text-heading-3">Assistance IA</h3>
            <AssistanceIa />
          </div>
        </section>
      )}

      {!partage && (
        <section aria-labelledby="titre-donnees" className="flex flex-col gap-3">
          <h2 id="titre-donnees" className="scroll-mt-8 text-heading-2">Données et à propos</h2>
          <DataBackup onImported={onImported} />

          {/* **Bien démarrer** (US-104), replié. L'écran s'affiche une fois à
              la première ouverture ; il reste ici pour la fois où l'on cherche
              « comment on installe ça, déjà ? » — la question se pose au
              deuxième téléphone, pas au premier.

              Un `<details>` et non une entrée de navigation de plus : six
              cartes dépliées en permanence dans les réglages seraient six
              cartes de plus à faire défiler pour atteindre la sauvegarde. */}
          <details className="rounded-card border border-border p-3">
            <summary className={`min-h-touch-min cursor-pointer list-none text-sm font-medium ${FOCUS_RING}`}>
              Bien démarrer avec Mijoté
            </summary>
            <div className="pt-3">
              <BienDemarrerCartes />
            </div>
          </details>
          {/* À propos (US-38). Le déploiement étant automatique, « quelle
              version ai-je sous les yeux ? » devient une question qu'on se pose
              pour de bon — typiquement quand une correction ne semble pas être
              arrivée. */}
          {sante === null ? (
            <p className="text-sm text-foreground-secondary">Version en cours de lecture…</p>
          ) : (
            <p className="text-sm text-foreground-secondary">
              Mijoté <span className="text-data">{sante.version}</span>
              {sante.commit !== undefined && <> · version <span className="text-data">{sante.commit}</span></>}
              {dateConstruction !== null && <> · déployée le {dateConstruction}</>}
            </p>
          )}
        </section>
      )}

      {partage && (
        <section aria-labelledby="titre-appareil-partage" className="flex flex-col gap-3">
          <h2 id="titre-appareil-partage" className="scroll-mt-8 text-heading-2">Cet appareil</h2>
          <p className="text-sm text-foreground-secondary">
            Cet appareil est en mode cuisine partagée : il n’a pas de compte, et le livre s’y consulte en lecture
            seule. Pour le remettre en mode normal, connectez-vous depuis un autre appareil et révoquez-le dans
            les réglages.
          </p>
        </section>
      )}
        </div>
      </div>
    </div>
  );
}
